import RootStore from "./stores/RootStore";
import rootStoreConfig, { apiClientOptions, hqMode } from "./rootStoreConfig";
import apiClient from "./api-client/apiClient";
import {
  installGlobalErrorHandlers,
  startDiagnostics,
} from "./diagnostics";
import { installDashboardHost } from "./views/dashboard/dashboardHost";
import { installReportsHost } from "./views/reports/ReportsHub/reportsHost";

interface Window {
  rootStore?: RootStore;
  dateFNS?: unknown;
  __STORE_COUNT__?: number;
  __MEMORY_MONITOR__?: unknown;
}

export function createRootStore(): RootStore {
  // Open the diagnostics boot window BEFORE anything can emit. The sink itself
  // is configured in rootStoreConfig (`diagnostics: hqDiagnosticsOptions`) and
  // constructed by RootStore; this only starts the boot-window timer and, in
  // dev, publishes `window.__hqDiagnostics`.
  startDiagnostics();

  // The Feathers client is built HERE, not inside the composition root. The
  // library RootStore requires an already-constructed client via `props.app`
  // and throws without one; `apiOptions` is logging/URL config and has never
  // built a client. Moving the call out is what lets HQ's remaining fork be
  // deleted later without also having to relocate the transport.
  const app = apiClient(hqMode, apiClientOptions);

  const rootStore = new RootStore({ ...rootStoreConfig, app });

  // The app's OWN unhandled failures (window.onerror / unhandledrejection)
  // into the same sink as the store layer's, so the admin panel is a complete
  // picture rather than a library-only one. Installed after construction
  // because it needs `rootStore.diagnostics`; anything that throws before this
  // point is a composition-root failure that would take the page down anyway.
  installGlobalErrorHandlers(rootStore);

  // The Developer Dashboard host seam: declares the dashboard's custom Feathers
  // methods on the socket client (not in test mode), installs the widget
  // registry + stock types, and registers HQ's own `hq.*` widget types once the
  // RouterStore has filled its route table (their targets are route keys that
  // do not exist synchronously after construction). After the error handlers so
  // anything it reports lands in the same sink. See views/dashboard/dashboardHost.ts.
  installDashboardHost(rootStore, app, { mode: hqMode });

  // The reports tool's host seam: declares @intrasync/reports' custom Feathers
  // methods on the socket client (not in test mode), installs the record-level
  // `rootStore.reportsClient`, and stamps the all-plant grant (`'*'`) the tool
  // reads on the signed-in user, matching the api's principal. See
  // views/reports/ReportsHub/reportsHost.ts.
  installReportsHost(rootStore, app, { mode: hqMode });

  // if (import.meta.env.MODE === "development") {
  (window as Window).rootStore = rootStore;
  // }

  // NOTE: the manual 401 handler that used to be wired here existed only for the
  // raw REST `createRestApi` client (now removed). ServiceStores own the
  // NotAuthenticated flow themselves — `ServiceStore.handleNotAuthenticated`
  // already runs `authStore.routerStoreAuthCheck()` (silent refresh) and the
  // router reroute — so no app-level wiring is needed.

  return rootStore;
}
